import { Component } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Table, Badge, Spinner } from "react-bootstrap";
import { FaUserFriends, FaFilePdf, FaExternalLinkAlt } from "react-icons/fa";
import { withTranslation } from "react-i18next";
import api from "../../services/api";
import { formataDataHoraExibicao, formataTelefoneExibicao } from "../../common/utils";

/**
 * Profissionais indicados ativos do médico do atendimento.
 *
 * O documento pede que, no cadastro do paciente, apareçam os dois profissionais: o médico
 * e quem ele indicou para acompanhar. Some quando o médico não tem indicação ativa — não
 * ocupa espaço no card à toa.
 */
class IndicadosDoMedico extends Component {
    constructor(props) {
        super(props);
        this.state = { indicacoes: [], carregando: false, aberto: false };
    }

    componentDidMount() {
        this.carregar();
    }

    componentDidUpdate(anterior) {
        if (anterior.medicoId !== this.props.medicoId || anterior.programaId !== this.props.programaId) {
            this.carregar();
        }
    }

    carregar = async () => {
        const { medicoId, programaId } = this.props;
        if (!medicoId) {
            this.setState({ indicacoes: [] });
            return;
        }

        this.setState({ carregando: true });
        try {
            const params = [`medico_id=${medicoId}`, "status=ativa", "limit=50"];
            if (programaId) params.push(`programa_id=${programaId}`);
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/?${params.join("&")}`);
            this.setState({ carregando: false, indicacoes: data.results || [] });
        } catch (error) {
            // bloco informativo: falhar aqui não pode atrapalhar o atendimento
            console.error("Erro ao carregar os profissionais indicados:", error);
            this.setState({ carregando: false, indicacoes: [] });
        }
    };

    renderModal = () => {
        const { t } = this.props;
        const { indicacoes, aberto } = this.state;
        if (!aberto) return null;

        return (
            <Modal show onHide={() => this.setState({ aberto: false })} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h5">{t("indicacoes:indicados.modalTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table responsive className="custom-table">
                        <thead>
                            <tr>
                                <th>{t("indicacoes:profissionais.table.professional")}</th>
                                <th>{t("indicacoes:profissionais.table.council")}</th>
                                <th>{t("indicacoes:indicados.contact")}</th>
                                <th>{t("indicacoes:profissionais.table.term")}</th>
                                <th className="text-end">{t("indicacoes:profissionais.table.declaration")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {indicacoes.map((i) => (
                                <tr key={i.id}>
                                    <td>
                                        {/* leva direto ao cadastro do provedor, sem passar por Provedores */}
                                        {i.dados_indicado ? (
                                            <a href={`/prestadores/edit/${i.dados_indicado.id}`}
                                               target="_blank" rel="noopener noreferrer">
                                                {i.dados_indicado.nome} <FaExternalLinkAlt size={10} />
                                            </a>
                                        ) : i.nome_indicado}
                                        <div className="text-muted small">
                                            {t("indicacoes:indicados.referredOn", { data: i.data_indicacao ? formataDataHoraExibicao(i.data_indicacao) : "-" })}
                                        </div>
                                    </td>
                                    <td>{[i.conselho, i.registro, i.registro_uf].filter(Boolean).join(" ") || "-"}</td>
                                    <td className="small">{formataTelefoneExibicao(i.telefone) || i.email || "-"}</td>
                                    <td>
                                        {!i.data_aceite_termo ? (
                                            <Badge bg="warning">{t("indicacoes:indicados.pending")}</Badge>
                                        ) : i.termo_comprovante_url ? (
                                            <a href={i.termo_comprovante_url} target="_blank"
                                               rel="noopener noreferrer" title={t("indicacoes:indicados.openSignedTerm")}>
                                                <Badge bg="success">{t("indicacoes:indicados.accepted")}</Badge>
                                            </a>
                                        ) : <Badge bg="success">{t("indicacoes:indicados.accepted")}</Badge>}
                                    </td>
                                    <td className="text-end">
                                        {i.declaracao_url ? (
                                            <Button variant="light" size="sm" href={i.declaracao_url}
                                                    target="_blank" rel="noopener noreferrer">
                                                <FaFilePdf className="me-1" /> {t("indicacoes:profissionais.view")}
                                            </Button>
                                        ) : <span className="text-muted small">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
            </Modal>
        );
    };

    render() {
        const { t } = this.props;
        const { indicacoes, carregando } = this.state;

        if (carregando) {
            return <p className="mb-1"><small>{t("indicacoes:indicados.label")} </small> <Spinner animation="border" size="sm" /></p>;
        }
        if (!indicacoes.length) return null;

        const nomes = indicacoes.map((i) => i.dados_indicado?.nome || i.nome_indicado).filter(Boolean);

        return (
            <>
                <p className="mb-1">
                    <small>{t("indicacoes:indicados.label")} </small> <strong>{nomes.join(", ")}</strong>
                </p>
                <Button variant="link" size="sm" className="p-0"
                        onClick={() => this.setState({ aberto: true })}>
                    <FaUserFriends className="me-1" /> {t("indicacoes:indicados.viewData")}
                </Button>
                {this.renderModal()}
            </>
        );
    }
}

IndicadosDoMedico.propTypes = {
    t: PropTypes.func.isRequired,
    medicoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default withTranslation(["indicacoes", "common"])(IndicadosDoMedico);
